import { Alert, Form, Input, Modal, Select, message } from "antd";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { FIRMS, FIRM_LABELS, Firm } from "@/modules/firms";
import {
  filterOperatorOption,
  operatorName,
} from "@/modules/integrations/accounts";
import type { Operator } from "@/providers/smarta-accounts/accounts-api-client";
import {
  createLead,
  LeadCreate,
  LeadEmailTakenError,
  setLeadAssignee,
} from "./api";
import { LEGAL_FORM_OPTIONS } from "./legalForms";
import type { LegalForm } from "./types";

/*
 * The modal body scrolls inside .ant-modal-wrap when the viewport is short. A
 * dropdown attached to <body> (antd's default) stays where it opened while the
 * form scrolls under it, so it drifts off its field and over other fields.
 * Rendering it next to its select, inside the dialog, keeps it attached. Same
 * approach as AntDeletePopupConfirm, the one existing getPopupContainer here.
 */
const popupInsideDialog = (trigger: HTMLElement) =>
  trigger.parentElement ?? document.body;

interface NewLeadFormValues {
  givenNames?: string;
  familyName: string;
  email: string;
  legalForm: LegalForm;
  firm: Firm;
  assignedOperatorId?: string;
}

const FIRM_OPTIONS = FIRMS.map((firm) => ({
  value: firm,
  label: FIRM_LABELS[firm],
}));

/*
 * Send only what was entered: an empty optional field is left out, never sent
 * as an empty string. A person with one official name has it as the family
 * name, so the given names are optional.
 */
const toPayload = (values: NewLeadFormValues): LeadCreate => {
  const givenNames = values.givenNames?.trim();
  return {
    legalForm: values.legalForm,
    firm: values.firm,
    mainTaxpayer: {
      ...(givenNames ? { givenNames } : {}),
      familyName: values.familyName.trim(),
      email: values.email.trim(),
    },
  };
};

interface Props {
  open: boolean;
  /** Loaded once by the page and shared with the table. */
  operators: Operator[];
  operatorsError: boolean;
  /** The signed-in operator's firm (the session's `firm` claim). */
  defaultFirm: Firm;
  onClose: () => void;
  onCreated: () => void;
}

export const NewLeadModal = ({
  open,
  operators,
  operatorsError,
  defaultFirm,
  onClose,
  onCreated,
}: Props) => {
  const { t } = useTranslation("Onboarding");
  const [form] = Form.useForm<NewLeadFormValues>();
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(false);
  // The button's loading state lags one render; the ref closes that gap.
  const savingRef = useRef(false);

  const close = () => {
    // Closing mid-request would reset the form, and the answer (a 409 or any
    // other error) would then land in a hidden, empty form.
    if (savingRef.current) return;
    form.resetFields();
    setFormError(false);
    onClose();
  };

  const submit = async (values: NewLeadFormValues) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setFormError(false);
    try {
      const lead = await createLead(toPayload(values));
      /*
       * smarta-accounts creates the customer without a manager, so the
       * assignee is a second request. The lead exists either way, so a failed
       * assignment closes the form like a success and says what is missing.
       */
      if (values.assignedOperatorId) {
        try {
          await setLeadAssignee(lead.publicId, values.assignedOperatorId);
        } catch {
          message.error(t("newLead.errors.assignee"));
        }
      }
      form.resetFields();
      onCreated();
    } catch (error) {
      if (error instanceof LeadEmailTakenError) {
        // The API's message says why; the fallback covers a 409 without one.
        form.setFields([
          {
            name: "email",
            errors: [error.message || String(t("newLead.errors.emailTaken"))],
          },
        ]);
      } else {
        setFormError(true);
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t("newLead.title")}
      okText={t("newLead.save")}
      cancelText={t("newLead.cancel")}
      okButtonProps={{ loading: saving }}
      cancelButtonProps={{ disabled: saving }}
      closable={!saving}
      keyboard={!saving}
      maskClosable={false}
      onOk={() => form.submit()}
      onCancel={close}
    >
      {formError && (
        <Alert
          type="error"
          showIcon
          message={t("newLead.errors.generic")}
          style={{ marginBottom: 16 }}
        />
      )}
      <Form<NewLeadFormValues>
        form={form}
        layout="vertical"
        name="new-lead"
        onFinish={submit}
      >
        <Form.Item name="givenNames" label={t("newLead.fields.givenNames")}>
          <Input autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="familyName"
          label={t("newLead.fields.familyName")}
          rules={[
            {
              required: true,
              whitespace: true,
              message: String(t("newLead.errors.familyNameRequired")),
            },
          ]}
        >
          <Input autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="email"
          label={t("newLead.fields.email")}
          rules={[
            {
              required: true,
              message: String(t("newLead.errors.emailRequired")),
            },
            {
              type: "email",
              message: String(t("newLead.errors.emailInvalid")),
            },
          ]}
        >
          <Input autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="legalForm"
          label={t("newLead.fields.legalForm")}
          rules={[
            {
              required: true,
              message: String(t("newLead.errors.legalFormRequired")),
            },
          ]}
        >
          <Select
            options={LEGAL_FORM_OPTIONS}
            getPopupContainer={popupInsideDialog}
          />
        </Form.Item>
        <Form.Item
          name="firm"
          label={t("newLead.fields.firm")}
          initialValue={defaultFirm}
          rules={[
            {
              required: true,
              message: String(t("newLead.errors.firmRequired")),
            },
          ]}
        >
          <Select
            options={FIRM_OPTIONS}
            getPopupContainer={popupInsideDialog}
          />
        </Form.Item>
        <Form.Item
          name="assignedOperatorId"
          label={t("newLead.fields.assignee")}
        >
          <Select
            allowClear
            showSearch
            filterOption={filterOperatorOption(operators)}
            getPopupContainer={popupInsideDialog}
            placeholder={t("newLead.unassigned")}
            notFoundContent={
              operatorsError ? t("newLead.errors.operators") : undefined
            }
            options={operators.map((operator) => ({
              value: operator.id,
              label: operatorName(operator),
            }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
